import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ScreeningStatusResponse } from '../api/risk-assessment-wizard';

interface ScreeningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True when initial screening request is in flight */
  isScreening: boolean;
  /** Polling status from the screening-status endpoint */
  screeningStatus?: ScreeningStatusResponse | null;
  onViewReport: () => void;
  onBackToDashboard: () => void;
  /** Called when user clicks retry after a failure */
  onRetry?: () => void;
}

/**
 * Modal that shows the progress of screening checks.
 *
 * States:
 * 1. Initial/Queuing: "Running screening checks..."
 * 2. Fast Checks Done: "Quick checks complete. Processing adverse media..."
 * 3. Processing: "Analyzing news articles..."
 * 4. Completed: "Report Generated Successfully"
 * 5. Failed: Error message with retry button
 */
export function ScreeningModal({
  open,
  onOpenChange,
  isScreening,
  screeningStatus,
  onViewReport,
  onBackToDashboard,
  onRetry,
}: ScreeningModalProps) {
  // Determine current state from polling status
  const status = screeningStatus?.status;

  const failed = status === 'FAILED';
  const fastChecksDone = status === 'FAST_CHECKS_DONE';
  const processing = status === 'PROCESSING';
  const generatingReport = status === 'GENERATING_REPORT';
  // When isScreening is true, we're waiting for a new processing cycle (e.g., EDD upgrade)
  // Don't consider status as completed even if it says COMPLETED - it's stale from previous screening
  const completed = status === 'COMPLETED' && !isScreening;
  // Show progress: during initial request OR while job is processing/generating
  const inProgress = isScreening || fastChecksDone || processing || generatingReport;

  // Derived states for progress indicators
  // Fast checks are done once we move past FAST_CHECKS_DONE
  // Also consider complete if isScreening is true (EDD upgrade) - fast checks were done in initial screening
  const fastChecksComplete =
    fastChecksDone || processing || generatingReport || completed || isScreening;
  // Adverse media analysis is only marked complete when fully done
  // This avoids showing both steps complete while still on "Report is Being Generated"
  const adverseMediaComplete = completed;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[390px]" hideCloseButton={completed}>
        <DialogHeader className="text-center sm:text-center">
          {/* Failed State */}
          {failed && (
            <>
              <div className="mx-auto mb-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
              </div>
              <DialogTitle className="text-[20px] font-semibold text-[#051923]">
                Screening Failed
              </DialogTitle>
              <DialogDescription className="pt-2 text-[16px] text-[#051923]">
                {screeningStatus?.adverseMedia.error || 'An error occurred during screening.'}
              </DialogDescription>
            </>
          )}

          {/* Completed State */}
          {completed && (
            <>
              <div className="mx-auto mb-4">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <DialogTitle className="text-[20px] font-semibold text-[#051923]">
                Report Generated Successfully
              </DialogTitle>
              <DialogDescription className="pt-2 text-[16px] text-[#051923]">
                Your risk assessment report is ready to view.
              </DialogDescription>
            </>
          )}

          {/* In Progress States */}
          {inProgress && !completed && !failed && (
            <>
              <DialogTitle className="text-[20px] font-semibold text-[#051923]">
                Report is Being Generated
              </DialogTitle>
              {/* Using div instead of DialogDescription to avoid hydration errors (div inside p) */}
              <div className="space-y-2 pt-2 text-center text-[16px] text-[#051923]">
                {/* Show progress steps */}
                <div className="flex flex-col items-center gap-2">
                  {/* Fast checks status */}
                  <div className="flex items-center gap-2">
                    {fastChecksComplete ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    )}
                    <span className={fastChecksComplete ? 'text-green-600' : ''}>
                      Running selected checks
                    </span>
                  </div>

                  {/* Adverse media status - show when queued OR when EDD upgrade is pending */}
                  {(screeningStatus?.adverseMedia.queued || isScreening) && (
                    <div className="flex items-center gap-2">
                      {adverseMediaComplete ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : processing || generatingReport || isScreening ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                      )}
                      <span
                        className={
                          adverseMediaComplete
                            ? 'text-green-600'
                            : processing || generatingReport || isScreening
                              ? ''
                              : 'text-gray-500'
                        }
                      >
                        {adverseMediaComplete
                          ? 'In-depth analysis complete'
                          : processing || generatingReport || isScreening
                            ? 'Analyzing news articles...'
                            : 'Adverse media check'}
                      </span>
                    </div>
                  )}
                </div>

                <p className="pt-2 text-sm text-gray-500">
                  If you navigate away, you will be notified when the report is ready.
                </p>
              </div>
            </>
          )}
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-3">
          {completed && (
            <>
              <Button
                onClick={onViewReport}
                className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90"
              >
                View Report
              </Button>
              <Button
                variant="outline"
                onClick={onBackToDashboard}
                className="h-[45px] w-full rounded-[5px] border-[1.5px] border-[#131313] bg-white text-[18px] font-medium leading-none tracking-normal text-[#131313] hover:bg-gray-50"
              >
                Back to Dashboard
              </Button>
            </>
          )}

          {failed && (
            <>
              {onRetry && (
                <Button
                  onClick={onRetry}
                  className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90"
                >
                  Retry Screening
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onBackToDashboard}
                className="h-[45px] w-full rounded-[5px] border-[1.5px] border-[#131313] bg-white text-[18px] font-medium leading-none tracking-normal text-[#131313] hover:bg-gray-50"
              >
                Back to Dashboard
              </Button>
            </>
          )}

          {inProgress && !completed && !failed && (
            <Button
              variant="outline"
              onClick={onBackToDashboard}
              className="h-[45px] w-full rounded-[5px] border-[1.5px] border-[#131313] bg-white text-[18px] font-medium leading-none tracking-normal text-[#131313] hover:bg-gray-50"
            >
              Back to Dashboard
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
